import React, { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { govDate, plural } from '../../format';
import {
  ENDING_LABEL,
  TIER_LABEL,
  goneSentence,
  seatRows,
  dayOf,
  lengthOf,
  matchesQuery,
  personTone,
  todayDay,
} from '../../timeline';
import type { SeatView, TenureView } from '../../types';
import { Marked, actName, postLabel } from './TimelineActs';

/**
 * Every post and who has held it: today's holder and since when, a thin
 * strip of its whole history, and the succession laid out on demand.
 */
export const TimelineSeats: React.FC<{
  seats: SeatView[];
  tenures: TenureView[];
  span: { from: number; to: number };
  query: string;
  selectedTenure: number | null;
  onSelectTenure: (index: number) => void;
  onSelectAct: (id: string) => void;
}> = ({ seats, tenures, span, query, selectedTenure, onSelectTenure, onSelectAct }) => {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const shown = useMemo(
    () =>
      seats.filter((seat) =>
        matchesQuery(
          query,
          postLabel(seat.post),
          // The bodies it replaced, and who held the post there, count too
          ...seatRows(seat).flatMap((row) => [
            row.label,
            ...row.tenures.flatMap((index) => [tenures[index].person, tenures[index].title]),
          ]),
        ),
      ),
    [seats, tenures, query],
  );
  const tiers = useMemo(() => {
    const byTier = new Map<SeatView['tier'], SeatView[]>();
    for (const seat of shown) byTier.set(seat.tier, [...(byTier.get(seat.tier) ?? []), seat]);
    return Array.from(byTier.entries()).sort((a, b) => a[0] - b[0]);
  }, [shown]);
  const today = todayDay();
  const width = span.to - span.from;
  const at = (day: number) => `${((day - span.from) / width) * 100}%`;

  /** One holder of a post, with the acts that began and ended it */
  const holder = (index: number, tenure: TenureView) => (
                        <li
                          key={index}
                          className={`flex flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded-lg px-2 py-1.5 text-[12.5px] ${
                            selectedTenure === index ? 'bg-white ring-1 ring-primary-600/40' : ''
                          }`}
                        >
                          <span
                            className="mt-1 h-2 w-2 flex-shrink-0 self-start rounded-full"
                            style={{ backgroundColor: personTone(tenure).edge }}
                          />
                          <button
                            type="button"
                            onClick={() => onSelectTenure(index)}
                            className="text-ink underline decoration-neutral-300 underline-offset-2 hover:decoration-neutral-500"
                          >
                            {tenure.person}
                          </button>
                          <span className="tabular-nums text-neutral-500">
                            {tenure.openStart ? 'by ' : ''}
                            {govDate(tenure.from)} – {tenure.to ? govDate(tenure.to) : 'today'}
                          </span>
                          <span className="text-neutral-400">· {lengthOf(tenure.from, tenure.to)}</span>
                          {tenure.ended && (
                            <span className="text-neutral-400">· {ENDING_LABEL[tenure.ended]?.toLowerCase()}</span>
                          )}
                          <span className="basis-full pl-4 text-[11.5px] text-neutral-400">
                            <button
                              type="button"
                              onClick={() => onSelectAct(tenure.fromAct.id)}
                              className="hover:text-ink"
                            >
                              {actName(tenure.fromAct)}
                            </button>
                            {tenure.toAct && (
                              <>
                                {' → '}
                                <button
                                  type="button"
                                  onClick={() => onSelectAct(tenure.toAct!.id)}
                                  className="hover:text-ink"
                                >
                                  {actName(tenure.toAct)}
                                </button>
                              </>
                            )}
                          </span>
                        </li>
  );

  if (!shown.length) {
    return (
      <p className="rounded-2xl border border-neutral-200 bg-white px-4 py-8 text-center text-[13px] text-neutral-400">
        No post matches.
      </p>
    );
  }

  return (
    <div className="space-y-5">
      {tiers.map(([tier, list]) => (
        <section key={tier}>
          <h3 className="mb-2 flex items-baseline gap-2 px-1">
            <span className="text-[15px] text-ink">{TIER_LABEL[tier]}</span>
            <span className="text-[12px] text-neutral-400">{plural(list.length, 'post')}</span>
          </h3>
          <ul className="divide-y divide-neutral-100 overflow-hidden rounded-2xl border border-neutral-200 bg-white">
            {list.map((seat) => {
              const history = seat.tenures.map((index) => ({ index, tenure: tenures[index] }));
              const before = seat.defunct ?? [];
              const earlier = before.flatMap((body) => body.tenures.map((index) => ({ index, tenure: tenures[index] })));
              const current = history.filter(({ tenure }) => !tenure.to);
              const last = history[history.length - 1];
              const isOpen = open.has(seat.key);
              return (
                <li key={seat.key}>
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() =>
                      setOpen((previous) => {
                        const next = new Set(previous);
                        if (next.has(seat.key)) next.delete(seat.key);
                        else next.add(seat.key);
                        return next;
                      })
                    }
                    className="grid w-full grid-cols-1 items-center gap-x-4 gap-y-2 px-4 py-3 text-left transition-colors hover:bg-neutral-50/70 sm:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,14rem)_1rem]"
                  >
                    <span className="min-w-0">
                      <span className="block break-words text-[13px] leading-snug text-ink">
                        <Marked text={seat.label} query={query} />
                      </span>
                      <span className="block text-[11.5px] text-neutral-400">{postLabel(seat.post)}</span>
                      {seat.ended && (
                        <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11.5px] text-neutral-400">
                          <DefunctTag />
                          {goneSentence(seat.ended)}
                        </span>
                      )}
                      {before.length > 0 && (
                        <span className="mt-0.5 block text-[11.5px] leading-snug text-neutral-400">
                          Replaced {before.map((body) => body.label).join(', ')}
                        </span>
                      )}
                    </span>
                    <span className="min-w-0">
                      {current.length ? (
                        current.map(({ index, tenure }) => (
                          <span key={index} className="flex min-w-0 items-center gap-2">
                            <span
                              className="h-2 w-2 flex-shrink-0 rounded-full"
                              style={{ backgroundColor: personTone(tenure).edge }}
                            />
                            <span className="min-w-0 truncate text-[12.5px] text-ink">
                              <Marked text={tenure.person} query={query} />
                              {tenure.acting && <span className="text-neutral-400"> · acting</span>}
                            </span>
                          </span>
                        ))
                      ) : (
                        <span className="text-[12.5px] text-neutral-400">
                          Nobody on file{last?.tenure.to ? ` since ${govDate(last.tenure.to)}` : ''}
                        </span>
                      )}
                      {current[0] && (
                        <span className="mt-0.5 block text-[11.5px] tabular-nums text-neutral-500">
                          {current[0].tenure.openStart ? 'In post by ' : 'Since '}
                          {govDate(current[0].tenure.from)}
                          <span className="text-neutral-400"> · {lengthOf(current[0].tenure.from, null)}</span>
                        </span>
                      )}
                    </span>
                    {/* the whole history, at a glance */}
                    <span className="relative block h-2 rounded-full bg-neutral-100" aria-hidden>
                      {[...earlier, ...history].map(({ index, tenure }) => {
                        const from = dayOf(tenure.from);
                        const to = tenure.to ? dayOf(tenure.to) : today;
                        return (
                          <span
                            key={index}
                            className="absolute inset-y-0 rounded-full"
                            style={{
                              left: at(from),
                              width: `max(3px, ${((to - from) / width) * 100}%)`,
                              backgroundColor: personTone(tenure).edge,
                              opacity: seat.tenures.includes(index) ? (tenure.to ? 0.45 : 0.9) : 0.22,
                            }}
                          />
                        );
                      })}
                    </span>
                    <ChevronDown
                      className={`hidden h-4 w-4 text-neutral-400 transition-transform sm:block ${isOpen ? 'rotate-180' : ''}`}
                      strokeWidth={1.75}
                    />
                  </button>

                  {isOpen && (
                    <div className="border-t border-neutral-100 bg-neutral-50/40 px-4 py-3">
                      <ol className="space-y-1">
                        {[...history].reverse().map(({ index, tenure }) => holder(index, tenure))}
                      </ol>
                      {/* The bodies that ended and were replaced by this one */}
                      {before.map((body) => (
                        <div key={body.label} className="mt-3 border-t border-dashed border-neutral-200 pt-2.5">
                          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 px-2 text-[12px] text-neutral-500">
                            <DefunctTag />
                            <Marked text={body.label} query={query} />
                          </p>
                          <p className="px-2 text-[11.5px] text-neutral-400">{goneSentence(body)}</p>
                          <ol className="mt-1 space-y-1">
                            {[...body.tenures].reverse().map((index) => holder(index, tenures[index]))}
                          </ol>
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
};

/** Marks a body that no longer exists */
const DefunctTag: React.FC = () => (
  <span className="rounded-full bg-neutral-200/70 px-1.5 py-px text-[9.5px] uppercase tracking-[0.05em] text-neutral-500">
    Defunct
  </span>
);
